import React, { memo } from "react";

import { ErrorBoundary } from "react-error-boundary";
import { Link } from "react-router-dom";
import {
  ClassificationIcon,
  LoadingIndicator,
  NoData,
  RenderWhen,
  ThreeDotsLoader
} from "../../../../../../components";
import Grid from "../../../../../../components/Grid";
import { NetworkRepEntry } from "../../../../../network/@types/Url";
import { Name } from "../../../../../search/schema/components";
import { SECTIONS } from "../../../../consts";
import commonStyles from "../../../../containers/report_summary/report-summary.module.scss";
import { pathToSS } from "../../../../util";
import SampleReportGridLink from "../../../details/SampleReportGrid/SampleReportGridLink";
import {
  hasAssociatedMalware,
  isRowMalwareConfigC2,
  MalwareConfigAnnotationIcons,
  malwareConfigStyles
} from "../../../malware_config";
import { NetworkRepWithSource } from "../../@types";
import { ANALYSIS } from "../../consts";
import styles from "./network-references.module.scss";
import NetworkReferenceReputation, {
  NetworkReferenceReputationGridTitle
} from "./NetworkReferenceReputation";

type SchemaProps = {
  hash: string | undefined;
  sourceId: number | undefined;
  relationshipId: number | undefined;
  enableSourceLink: boolean;
};

const NetworkReferenceErrorComp = () => (
  <span className={commonStyles.error_msg}>Network References Error</span>
);
const NetworkReferenceEmptyGridComp = () => (
  <span className={commonStyles.error_msg}>
    No available Network References
  </span>
);

const isMediaAddress = (source: string) =>
  source === ANALYSIS.MEDIAADDRESS ? 0 : 1;

const orderByMediaAddress = (sources: NetworkRepWithSource["extra"]) =>
  sources
    .slice()
    .sort((a, b) => isMediaAddress(a.source) - isMediaAddress(b.source));

const getUniqueSources = (sources: NetworkRepWithSource["extra"]) =>
  Array.from(new Set(orderByMediaAddress(sources).map(({ source }) => source)));

const networkRefSchema = ({
  hash,
  sourceId,
  relationshipId,
  enableSourceLink
}: SchemaProps) => ({
  fields: [
    {
      name: "status",
      displayTitle: (
        <span
          style={{ opacity: "0.3" }}
          className="threat threat-placeholder"
        />
      ),
      sortable: false,
      component: (source: NetworkRepEntry) => (
        <ClassificationIcon classification={source.classification} />
      ),
      style: { width: "5%" },
      className: "threat-table-cell"
    },
    {
      name: "reputation",
      displayTitle: NetworkReferenceReputationGridTitle,
      sortable: false,
      component: (item: NetworkRepWithSource) => (
        <NetworkReferenceReputation networkRepEntry={item} />
      ),
      style: {
        width: "12%"
      }
    },
    {
      name: "malware_config",
      displayTitle: "",
      sortable: false,
      component: (item: NetworkRepWithSource) => (
        <MalwareConfigAnnotationIcons
          showMaliciousFiles={hasAssociatedMalware(item)}
          showMalwareConfig={isRowMalwareConfigC2(item)}
          variant="c2"
        />
      ),
      style: { width: "4%" }
    },
    {
      name: "value",
      displayTitle: "Value",
      sortable: false,
      component: (item: NetworkRepEntry) => (
        <Name
          data={{
            int_submission_analyzer: 1,
            classification: item?.classification,
            // urls are normalized to have protocol in case that it's missing so we wan't that value for the filename
            file_name: item?.normalized_url || item?.requested_network_location
          }}
        />
      ),
      style: {
        width: "24%"
      }
    },
    {
      name: "source",
      displayTitle: "Source",
      sortable: false,
      component: ({ extra }: NetworkRepWithSource) => {
        if (!extra) return <NoData />;

        const uniqueSources = getUniqueSources(extra);

        if (!enableSourceLink) {
          return uniqueSources.join(", ");
        }

        return (
          <div className="overflow-ellipsis">
            {uniqueSources.map((source, index, array) => (
              <span key={source}>
                <Link
                  className={styles.source_link}
                  to={`${pathToSS(
                    hash,
                    sourceId,
                    SECTIONS.FILE_ANALYSIS.NETWORK_REFERENCES,
                    relationshipId
                  )}?filterSource=${encodeURIComponent(source)}`}
                >
                  {source}
                </Link>
                {index < array.length - 1 && ", "}
              </span>
            ))}
          </div>
        );
      },
      style: { width: "20%" },
      className: "overflow-ellipsis"
    },
    {
      name: "file_protocol",
      displayTitle: "File",
      sortable: false,
      style: { width: "17%" },
      className: "overflow-ellipsis",
      component: (item: NetworkRepWithSource) => {
        // remove non static analysis network reputations (interesting strings only)
        if (item.extra.every(e => !e.filename)) {
          return <NoData />;
        }

        const sourcesMap = new Map(item.extra.map(e => [e.filename, e]));
        const sources = Array.from(sourcesMap.values()).filter(
          source => source.filename
        );

        return (
          <div className="overflow-ellipsis pdf-network-references-files-list">
            {sources.map((source, index) => (
              <span key={source.filename}>
                <SampleReportGridLink
                  data={source}
                  sampleSourceID={sourceId}
                  relationshipID={relationshipId}
                  section={"ticore/interesting_strings"}
                />
                {index < sources.length - 1 && (
                  <span className="pdf-hide-in-pdf">, </span>
                )}
              </span>
            ))}
          </div>
        );
      }
    },
    {
      name: "categories",
      displayTitle: "Categories",
      sortable: false,
      component: ({
        categories,
        categoriesLoading
      }: NetworkRepEntry & { categoriesLoading?: boolean }) => (
        <RenderWhen
          condition={!categoriesLoading}
          fallback={<ThreeDotsLoader />}
        >
          {categories?.length ? (
            <span title={categories.join(", ")}>{categories.join(", ")}</span>
          ) : (
            <NoData />
          )}
        </RenderWhen>
      ),
      style: { width: "18%" },
      className: "overflow-ellipsis"
    }
  ]
});

interface Props {
  data: NetworkRepWithSource[] | undefined;
  hash: string | undefined;
  sourceId: number | undefined;
  relationshipId: number | undefined;
  isLoading: boolean;
  hidePaging: boolean;
  enableSourceLink?: boolean;
  page?: number;
  pageSize?: number;
  totalResults?: number;
  numPages?: number;
  onPageChange?: (newPage: number, newPageSize: number) => void;
}

const NetworkReferencesGrid: React.FC<Props> = ({
  data,
  hash,
  sourceId,
  relationshipId,
  isLoading,
  hidePaging,
  enableSourceLink = false,
  page,
  pageSize,
  totalResults,
  numPages,
  onPageChange
}) => (
  <div data-testid={"network-references-grid"}>
    <ErrorBoundary fallback={<NetworkReferenceErrorComp />}>
      <Grid
        data={data || []}
        hidePaging={hidePaging}
        enablePageSizeSelector
        schema={networkRefSchema({
          hash,
          sourceId,
          relationshipId,
          enableSourceLink
        })}
        showLoading={isLoading}
        shouldMarkRow={(rowItem: NetworkRepWithSource) => ({
          markRow: isRowMalwareConfigC2(rowItem),
          markRowClassName: malwareConfigStyles.annotated_row
        })}
        emptyGridMsg={<NetworkReferenceEmptyGridComp />}
        page={page}
        pageSize={pageSize}
        totalResults={totalResults}
        numPages={numPages}
        onPageChange={onPageChange}
        showPageNumbers
        LoadingComponent={<LoadingIndicator style={{ margin: "15rem 0" }} />}
      />
    </ErrorBoundary>
  </div>
);

export default memo(NetworkReferencesGrid);
